import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Select,
  Tag,
  Upload,
  message,
  Space,
  Divider,
  Typography
} from 'antd';
import {
  SaveOutlined,
  UploadOutlined,
  BoldOutlined,
  ItalicOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  LinkOutlined,
  PictureOutlined,
  CodeOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { mockPosts, mockCategories, mockTags } from '../../api/mockData';
import './index.css';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const WritePost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [preview, setPreview] = useState(false);
  const [content, setContent] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [coverImage, setCoverImage] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (id) {
      // 编辑模式，加载现有文章
      const post = mockPosts.find(p => p.id === id);
      if (post) {
        setIsEditing(true);
        form.setFieldsValue({
          title: post.title,
          category: post.category,
          excerpt: post.excerpt
        });
        setContent(post.content);
        setSelectedTags(post.tags);
        setCoverImage(post.coverImage);
      }
    }
  }, [id, form]);

  const handleTagChange = (value) => {
    setSelectedTags(value);
  };

  const handleContentChange = (e) => {
    setContent(e.target.value);
  };

  const handleUpload = (info) => {
    if (info.file.status === 'done') {
      message.success(`${info.file.name} 上传成功`);
      setCoverImage(info.file.response?.url || info.file.url);
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} 上传失败`);
    }
  };

  const handleSaveDraft = () => {
    message.success('草稿已保存');
  };

  const handlePublish = (values) => {
    const postData = {
      ...values,
      content,
      tags: selectedTags,
      coverImage: coverImage || 'https://picsum.photos/800/400?random=' + Math.random(),
      date: new Date().toISOString().split('T')[0],
      views: 0,
      likes: 0,
      comments: 0
    };

    console.log('发布文章:', postData);
    message.success(isEditing ? '文章更新成功' : '文章发布成功');
    navigate('/');
  };

  const insertMarkdown = (type) => {
    const textarea = document.querySelector('.editor-textarea textarea');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    let newText = '';

    switch (type) {
      case 'bold':
        newText = `**${selectedText || '粗体文字'}**`;
        break;
      case 'italic':
        newText = `*${selectedText || '斜体文字'}*`;
        break;
      case 'link':
        newText = `[${selectedText || '链接文字'}](https://example.com)`;
        break;
      case 'image':
        newText = `![${selectedText || '图片描述'}](https://example.com/image.jpg)`;
        break;
      case 'code':
        newText = selectedText ? `\`\`\`\n${selectedText}\n\`\`\`` : '```\n代码块\n```';
        break;
      case 'ul':
        newText = selectedText ? `- ${selectedText}` : '- 列表项';
        break;
      case 'ol':
        newText = selectedText ? `1. ${selectedText}` : '1. 列表项';
        break;
      default:
        return;
    }

    const newContent = 
      content.substring(0, start) + 
      newText + 
      content.substring(end);
    
    setContent(newContent);
    
    // 聚焦到textarea
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + newText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  return (
    <div className="write-post-page">
      <div className="container">
        <Card>
          <div className="write-post-header">
            <Title level={3}>{isEditing ? '编辑文章' : '写新文章'}</Title>
            <Space>
              <Button onClick={handleSaveDraft}>保存草稿</Button>
              <Button 
                type={preview ? 'default' : 'primary'} 
                onClick={() => setPreview(!preview)}
              >
                {preview ? '返回编辑' : '预览'}
              </Button>
            </Space>
          </div>

          <Divider />

          {preview ? (
            <div className="preview-container">
              <div className="preview-header">
                <Title level={2}>{form.getFieldValue('title') || '文章标题'}</Title>
                {coverImage && (
                  <div className="preview-cover">
                    <img src={coverImage} alt="封面预览" />
                  </div>
                )}
              </div>
              <div className="preview-content">
                <ReactMarkdown className="markdown-content">
                  {content || '在这里输入文章内容...'}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            <Form
              form={form}
              layout="vertical"
              onFinish={handlePublish}
              className="write-post-form"
            >
              {/* 封面图上传 */}
              <Form.Item label="封面图片">
                <Upload
                  name="cover"
                  listType="picture-card"
                  showUploadList={false}
                  onChange={handleUpload}
                  action="https://www.mocky.io/v2/5cc8019d300000980a055e76" // 模拟上传地址
                >
                  {coverImage ? (
                    <img src={coverImage} alt="cover" className="cover-preview" />
                  ) : (
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>上传封面</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>

              <Form.Item
                name="title"
                label="文章标题"
                rules={[{ required: true, message: '请输入文章标题' }]}
              >
                <Input placeholder="请输入文章标题" size="large" />
              </Form.Item>

              <Form.Item
                name="category"
                label="分类"
                rules={[{ required: true, message: '请选择分类' }]}
              >
                <Select placeholder="请选择分类" size="large">
                  {mockCategories.map(category => (
                    <Option key={category} value={category}>{category}</Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="标签">
                <Select
                  mode="multiple"
                  placeholder="选择标签"
                  value={selectedTags}
                  onChange={handleTagChange}
                  size="large"
                >
                  {mockTags.map(tag => (
                    <Option key={tag} value={tag}>
                      <Tag>{tag}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="excerpt"
                label="文章摘要"
                rules={[{ required: true, message: '请输入文章摘要' }]}
              >
                <TextArea 
                  placeholder="请输入文章摘要" 
                  rows={3} 
                  showCount 
                  maxLength={200}
                />
              </Form.Item>

              <Form.Item
                label="文章内容"
                rules={[{ required: true, message: '请输入文章内容' }]}
              >
                <div className="editor-toolbar">
                  <Space wrap>
                    <Button 
                      type="text" 
                      icon={<BoldOutlined />} 
                      onClick={() => insertMarkdown('bold')}
                      title="加粗"
                    />
                    <Button 
                      type="text" 
                      icon={<ItalicOutlined />} 
                      onClick={() => insertMarkdown('italic')}
                      title="斜体"
                    />
                    <Button 
                      type="text" 
                      icon={<LinkOutlined />} 
                      onClick={() => insertMarkdown('link')}
                      title="链接"
                    />
                    <Button 
                      type="text" 
                      icon={<PictureOutlined />} 
                      onClick={() => insertMarkdown('image')}
                      title="图片"
                    />
                    <Button 
                      type="text" 
                      icon={<CodeOutlined />} 
                      onClick={() => insertMarkdown('code')}
                      title="代码块"
                    />
                    <Button 
                      type="text" 
                      icon={<UnorderedListOutlined />} 
                      onClick={() => insertMarkdown('ul')}
                      title="无序列表"
                    />
                    <Button 
                      type="text" 
                      icon={<OrderedListOutlined />} 
                      onClick={() => insertMarkdown('ol')}
                      title="有序列表"
                    />
                  </Space>
                </div>
                <div className="editor-textarea">
                  <TextArea
                    value={content}
                    onChange={handleContentChange}
                    placeholder="使用 Markdown 语法编写文章..."
                    rows={20}
                    className="markdown-editor"
                  />
                </div>
                <div className="editor-hint">
                  <small>支持 Markdown 语法，点击工具栏按钮可快速插入格式</small>
                </div>
              </Form.Item>

              <Divider />

              <Form.Item className="form-actions">
                <Space size="large">
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    size="large"
                    icon={<SaveOutlined />}
                  >
                    {isEditing ? '更新文章' : '发布文章'}
                  </Button>
                  <Button 
                    size="large"
                    onClick={() => navigate('/')}
                  >
                    取消
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default WritePost;